import { useMemo } from "react";
import { CalendarDays, Pill, StickyNote } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

function dateLabel(value: string) {
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

export function DateOnlyImportEventsPanel({ events = [] }: { events?: any[] }) {
  const groups = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const event of events) {
      const date = String(event.local_date ?? "sem-data");
      const list = map.get(date) ?? [];
      list.push(event);
      map.set(date, list);
    }
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [events]);

  if (!events.length) return null;

  return (
    <Card className="overflow-hidden border-info/25">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b bg-info/5 p-4">
        <div>
          <div className="flex items-center gap-2 font-semibold"><CalendarDays className="h-4 w-4 text-info" />Eventos importados sem horário</div>
          <div className="mt-1 text-xs text-muted-foreground">Preservados por data. Eles não participam das curvas PK/PD intradiárias até que um horário real seja confirmado.</div>
        </div>
        <Badge variant="outline">{events.length} evento(s)</Badge>
      </div>
      <div className="divide-y">
        {groups.map(([date, rows], groupIndex) => (
          <details key={date} open={groupIndex < 2} className="group">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-3">
              <span className="text-sm font-medium">{date === "sem-data" ? "Data inválida" : dateLabel(date)}</span>
              <Badge variant="secondary">{rows.length}</Badge>
            </summary>
            <div className="divide-y border-t bg-muted/10">
              {rows.map((event) => {
                const Icon = event.event_type === "note" ? StickyNote : Pill;
                const amount = event.dose_amount != null ? `${event.dose_amount}${event.dose_unit ? ` ${event.dose_unit}` : ""}` : null;
                return (
                  <div key={event.id} className="flex min-w-0 gap-3 p-3">
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-muted text-muted-foreground"><Icon className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="break-words text-sm font-medium">{[event.raw_name || (event.event_type === "note" ? "Nota" : "Evento"), amount].filter(Boolean).join(" · ")}</div>
                      {event.note ? <div className="mt-0.5 whitespace-pre-wrap break-words text-xs text-muted-foreground">{event.note}</div> : null}
                      <div className="mt-1 flex flex-wrap gap-1"><Badge variant="outline" className="text-[9px]">{event.source_app}</Badge><Badge variant="secondary" className="text-[9px]">confiança {event.confidence}</Badge>{event.warnings?.length ? <Badge variant="outline" className="text-[9px]">{event.warnings.length} alerta(s)</Badge> : null}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        ))}
      </div>
    </Card>
  );
}
